import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import ProjectsService from "../../api/projectsService";
import TechnologiesService from "../../api/technologiesService";
import UploadService from "../../api/uploadService";
import MultipleImageUploader from "../../components/common/MultipleImageUploader";
import SingleImageUploader from "../../components/common/SingleImageUploader";

const EditProjectPage = () => {
  const { id } = useParams();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      startYear: "",
      endYear: "",
      isActive: true,
      technologiesIds: [],
    },
  });

  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailData, setThumbnailData] = useState(null);
  const [projectImages, setProjectImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [project, techData] = await Promise.all([
          ProjectsService.getById(id),
          TechnologiesService.getAll(),
        ]);

        setTechnologies(techData);
        reset({
          name: project.name,
          description: project.description,
          startYear: project.startYear || "",
          endYear: project.endYear || "",
          isActive: project.isActive,
          technologiesIds: project.technologies?.map((tech) => tech.id) || [],
        });

        // Set thumbnail if it exists
        if (project.thumbnail) {
          setThumbnailData({
            previewUrl: project.thumbnail,
            isExisting: true,
          });
        }

        // Set project images if they exist
        if (project.images && project.images.length > 0) {
          const formattedImages = project.images.map((url) => ({
            url,
            previewUrl: url,
            isExisting: true,
          }));
          setProjectImages(formattedImages);
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
        alert("Could not load project. Please try again.");
        navigate("/admin/projects");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, reset]);

  const handleThumbnailSelected = (fileData) => {
    setThumbnailData(fileData);
  };

  const handleThumbnailRemoved = () => {
    // Release object URL to prevent memory leaks
    if (thumbnailData?.previewUrl && !thumbnailData.isExisting) {
      URL.revokeObjectURL(thumbnailData.previewUrl);
    }
    setThumbnailData(null);
  };

  const handleProjectImagesUpdate = (updatedImages) => {
    setProjectImages(updatedImages);
  };

  // Upload a single file and return its URL
  const uploadFile = async (fileData) => {
    if (!fileData || !fileData.file) return null;
    try {
      const result = await UploadService.uploadImage(fileData.file);
      return result.url;
    } catch (error) {
      console.error(`Error uploading ${fileData.fileName}:`, error);
      throw new Error(`Failed to upload ${fileData.fileName}`);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Upload or use existing thumbnail
      let thumbnailUrl = null;
      if (thumbnailData) {
        if (thumbnailData.file) {
          // Upload new thumbnail
          thumbnailUrl = await uploadFile(thumbnailData);
          if (!thumbnailUrl) {
            throw new Error("Failed to upload thumbnail image");
          }
        } else if (thumbnailData.isExisting) {
          // Use existing thumbnail
          thumbnailUrl = thumbnailData.previewUrl;
        }
      }

      // Upload or use existing project images
      const imageUrls = [];
      for (const imageData of projectImages) {
        if (imageData.isNew && imageData.file) {
          // Upload new image
          const uploadedUrl = await uploadFile(imageData);
          if (uploadedUrl) {
            imageUrls.push(uploadedUrl);
          }
        } else if (imageData.isExisting || imageData.url) {
          // Use existing image
          imageUrls.push(imageData.url || imageData.previewUrl);
        }
      }

      // Prepare data for submission
      const dataToSubmit = {
        ...formData,
        thumbnail: thumbnailUrl,
        images: imageUrls,
        startYear: formData.startYear ? parseInt(formData.startYear, 10) : null,
        endYear: formData.endYear ? parseInt(formData.endYear, 10) : null,
      };

      await ProjectsService.update(id, dataToSubmit);
      navigate("/admin/projects");
    } catch (error) {
      console.error("Error updating project:", error);
      alert("Failed to update project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Project
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item size={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Project name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Project Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Project Description"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="startYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Start Year"
                    type="number"
                    inputProps={{ min: 1990, max: new Date().getFullYear() }}
                    error={!!errors.startYear}
                    helperText={errors.startYear?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="endYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="End Year"
                    type="number"
                    inputProps={{
                      min: 1990,
                      max: new Date().getFullYear() + 10,
                    }}
                    helperText="Leave empty if project is ongoing"
                    error={!!errors.endYear}
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...field}
                      />
                    }
                    label="Project is active/published"
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Thumbnail Image
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                This will be the main image displayed for the project in lists
                and cards.
              </Typography>
              <SingleImageUploader
                currentImage={thumbnailData?.previewUrl}
                onFileSelected={handleThumbnailSelected}
                onRemove={handleThumbnailRemoved}
                buttonLabel="Select Thumbnail"
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Project Images
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                Add multiple images showcasing different aspects of your
                project.
              </Typography>
              <MultipleImageUploader
                images={projectImages}
                onImagesUpdate={handleProjectImagesUpdate}
                buttonLabel="Add Project Images"
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Technologies Used
              </Typography>
              <Controller
                name="technologiesIds"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    multiple
                    renderValue={(selected) => {
                      return technologies
                        .filter((tech) => selected.includes(tech.id))
                        .map((tech) => tech.name)
                        .join(", ");
                    }}
                  >
                    {technologies.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid>

            <Grid item size={12} sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    {(thumbnailData && thumbnailData.file) ||
                    projectImages.some((img) => img.isNew)
                      ? "Uploading Images..."
                      : "Updating Project..."}
                  </>
                ) : (
                  "Update Project"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/projects")}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditProjectPage;
